"use client";

import dynamic from "next/dynamic";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Ref,
} from "react";
import type { BeforeMount, EditorProps, OnMount } from "@monaco-editor/react";
import { bumpFontSize, DEFAULT_EDITOR_SETTINGS } from "../lib/editor-settings";
import type { EditorSettings } from "../lib/editor-settings";
import {
  FormatError,
  formatSourceWithWasm,
  formatSupportFor,
  minimalEdit,
} from "../lib/format-code";
import { MONACO_VS_PATH } from "../lib/monaco-assets.generated";
import { registerProgsityDarkTheme } from "../lib/progsity-monaco-theme";
import { cn } from "@/lib/utils";
import styles from "./compile-markers.module.css";

const MonacoEditor = dynamic(
  async () => {
    const mod = await import("@monaco-editor/react");
    // Same-origin Monaco (`PG-PWA01`, ADR-0052). Without this the loader
    // fetches the editor from jsdelivr, which the offline shell worker could
    // only keep as opaque responses; the versioned path under `public/monaco`
    // is written by `scripts/sync-monaco-assets.mjs` and cached first by the
    // worker. `config` only merges state, so calling it here, inside the same
    // split chunk as the editor, keeps the main bundle unchanged.
    mod.loader.config({ paths: { vs: MONACO_VS_PATH } });
    return mod.default;
  },
  {
    ssr: false,
    loading: () => (
      <div
        className="flex min-h-[280px] w-full flex-1 animate-pulse items-center justify-center rounded-lg bg-[#0C1220] lg:min-h-0"
        aria-hidden
      >
        <div className="h-8 w-48 rounded bg-[#E94D1D]/20" />
      </div>
    ),
  },
);

const NARROW_VIEWPORT_MQ = "(max-width: 1023px)";

function subscribeNarrowViewport(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const mq = window.matchMedia(NARROW_VIEWPORT_MQ);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function getNarrowViewportSnapshot() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(NARROW_VIEWPORT_MQ).matches;
}

function getNarrowViewportServerSnapshot() {
  return false;
}

type EditorInstance = Parameters<OnMount>[0];
type MonacoInstance = Parameters<OnMount>[1];

export type FormatOutcome = "formatted" | "unchanged" | "unsupported";

/** Imperative surface for the toolbar: `PG-IDE02`. */
export interface PlaygroundEditorHandle {
  /**
   * Formats the buffer in place as one undo step. Resolves `unsupported` for a
   * language without a reliable formatter; rejects with a `FormatError` when
   * the formatter could not load or the source does not parse.
   */
  format: () => Promise<FormatOutcome>;
  /**
   * Replaces the buffer with a stored version as **one** undo step
   * (`PG-IDE05`).
   *
   * `setValue` would work and is wrong: it clears Monaco's undo stack, so a
   * restore the owner did not mean would be unrecoverable. This is the same
   * minimal-edit-between-undo-stops sandwich `format` uses, so Ctrl+Z brings
   * back exactly the buffer that was there, and the change flows through
   * `onChange` so the dirty state moves as if it had been typed.
   *
   * Returns `false` when the buffer already holds that text, so the caller can
   * skip a pointless save.
   */
  restore: (text: string) => boolean;
  revealLine: (line: number, column?: number) => boolean;
}

export type EditorMarker = { line: number; column: number; message: string };
const NO_MARKERS: readonly EditorMarker[] = [];

type PlaygroundEditorProps = {
  ref?: Ref<PlaygroundEditorHandle>;
  className?: string;
  /** SEO split view: shrink inside a fixed-height pane instead of min 50vh. */
  fillParent?: boolean;
  /**
   * `EX-EDR01`: take the container's full height at every width. Without it a
   * phone gets a fixed band (`clamp(220px, ...)`), which the Playground wants
   * and the Judge's editor column, where the editor is the whole pane, does not.
   */
  fillHeight?: boolean;
  language: string;
  value: string;
  onChange: (value: string) => void;
  settings: EditorSettings;
  /** Called when the user zooms (Ctrl/Cmd + wheel or +/-) — persists via parent editor settings. */
  onFontSizeChange?: (fontSize: number) => void;
  onRun: () => void;
  /**
   * Shift+Alt+F inside the editor (`PG-IDE02`). The owner runs the same handler
   * the toolbar button does, so the feedback is identical. Absent (the exam and
   * judge workspaces), the chord keeps Monaco's own Format Document behaviour.
   */
  onFormatShortcut?: () => void;
  /**
   * Every keydown inside the editor — `EX-AUT01`, `docs/27` §9.17.
   *
   * Wired to Monaco's own key stream rather than a DOM listener on the container,
   * because Monaco cancels propagation for keys it has bound: a chord the exam
   * itself registered would otherwise be invisible to a listener above it. The
   * consumer decides what counts and stores nothing about the key.
   *
   * Absent everywhere except the exam workspace — the Playground counts nothing.
   */
  onKeyDown?: (event: KeyboardEvent) => void;
  /**
   * `EX-QA02`: threaded into Monaco's own `options.ariaLabel` rather than a
   * top-level DOM `aria-label`: the `@monaco-editor/react` wrapper is a React
   * component, not a DOM node, so an `aria-label` prop passed straight to it
   * (as `"aria-label"` below still is, by earlier callers) is not a recognised
   * prop and is silently dropped. Prefer this one on new call sites.
   */
  ariaLabel?: string;
  /** @deprecated pass `ariaLabel` instead, kept so existing call sites keep compiling. */
  "aria-label"?: string;
  /**
   * `EX-PUB21`: the dark theme to use when the reader's setting is dark. The
   * Judge's editors pass `progsity-judge`, the Judge bar's near-black; every
   * other caller keeps the Playground's own.
   */
  darkTheme?: "progsity-dark" | "progsity-judge";
  /**
   * `EX-EDR03`: a viewer, not an editor (the upload dialog's preview of the
   * whole file): Monaco's own `readOnly` and `domReadOnly`, so it highlights
   * and scrolls exactly as the editor beside it does and no key edits it.
   */
  readOnly?: boolean;
  /**
   * `EX-EDO01`: Monaco's right-click menu, on by default. The Editor only
   * level turns it off: its Paste reads the clipboard through the Clipboard
   * API, which fires no paste event for the level's rule to judge.
   */
  contextMenu?: boolean;
  /** EX-EDR04: attributed compiler errors, never runtime traces. */
  markers?: readonly EditorMarker[];
};

export function PlaygroundEditor({
  ref,
  className,
  fillParent = false,
  fillHeight = false,
  language,
  value,
  onChange,
  settings,
  onFontSizeChange,
  onRun,
  onFormatShortcut,
  onKeyDown,
  ariaLabel,
  "aria-label": legacyAriaLabel,
  darkTheme = "progsity-dark",
  readOnly = false,
  contextMenu = true,
  markers = NO_MARKERS,
}: PlaygroundEditorProps) {
  const resolvedAriaLabel = ariaLabel ?? legacyAriaLabel;
  const isNarrowViewport = useSyncExternalStore(
    subscribeNarrowViewport,
    getNarrowViewportSnapshot,
    getNarrowViewportServerSnapshot,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditorInstance | null>(null);
  const pendingReveal = useRef<{ line: number; column: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  const markerDecorations = useRef<ReturnType<EditorInstance["createDecorationsCollection"]> | null>(null);
  const modelListener = useRef<{ dispose: () => void } | null>(null);
  const monacoRef = useRef<MonacoInstance | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const visualViewportCleanupRef = useRef<(() => void) | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;

  const onFontSizeChangeRef = useRef(onFontSizeChange);
  onFontSizeChangeRef.current = onFontSizeChange;

  const onKeyDownRef = useRef(onKeyDown);
  onKeyDownRef.current = onKeyDown;

  const onFormatShortcutRef = useRef(onFormatShortcut);
  onFormatShortcutRef.current = onFormatShortcut;

  const cleanupWheelRef = useRef<(() => void) | null>(null);
  const keyListenerRef = useRef<{ dispose: () => void } | null>(null);

  useEffect(() => {
    return () => {
      cleanupWheelRef.current?.();
      cleanupWheelRef.current = null;
      keyListenerRef.current?.dispose();
      keyListenerRef.current = null;
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      visualViewportCleanupRef.current?.();
      visualViewportCleanupRef.current = null;
      const model = editorRef.current?.getModel();
      if (model) monacoRef.current?.editor.setModelMarkers(model, "progsity-compile", []);
      markerDecorations.current?.clear();
      modelListener.current?.dispose();
      editorRef.current = null;
      monacoRef.current = null;
    };
  }, []);

  /**
   * Format Code (`PG-IDE02`). JavaScript and TypeScript go through Monaco's own
   * action, which the bundled TypeScript service formats. The wasm languages
   * format the buffer as a string and apply the smallest edit that reproduces
   * the result, between two undo stops: one undo step, cursor and scroll left
   * where they were unless they sat inside the changed region, and the normal
   * `onChange` path so the parent's dirty state moves exactly as if typed.
   */
  const format = useCallback(async (): Promise<FormatOutcome> => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel();
    if (!editor || !monaco || !model) {
      throw new FormatError("unknown", "The editor is not ready yet.");
    }
    const support = formatSupportFor(language);
    if (support === "unsupported") return "unsupported";

    if (support === "monaco") {
      const before = model.getValue();
      const action = editor.getAction("editor.action.formatDocument");
      if (!action) throw new FormatError("unknown", "Monaco has no format action.");
      await action.run();
      return model.getValue() === before ? "unchanged" : "formatted";
    }

    // The first click may wait on a multi-megabyte download. A student who
    // kept typing meanwhile must not have that text replaced by a result
    // computed for an older buffer, so the formatter runs against whatever the
    // buffer holds once it is loaded.
    let source = model.getValue();
    let formatted = await formatSourceWithWasm(language, source, { tabSize: settings.tabSize });
    if (model.getValue() !== source) {
      source = model.getValue();
      formatted = await formatSourceWithWasm(language, source, { tabSize: settings.tabSize });
    }
    const edit = minimalEdit(source, formatted);
    if (!edit) return "unchanged";

    const range = monaco.Range.fromPositions(
      model.getPositionAt(edit.start),
      model.getPositionAt(edit.end),
    );
    editor.pushUndoStop();
    editor.executeEdits("progsity-format", [{ range, text: edit.text, forceMoveMarkers: true }]);
    editor.pushUndoStop();
    return "formatted";
  }, [language, settings.tabSize]);

  const restore = useCallback((text: string): boolean => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel();
    if (!editor || !monaco || !model) return false;

    const current = model.getValue();
    const edit = minimalEdit(current, text);
    if (!edit) return false;

    const range = monaco.Range.fromPositions(
      model.getPositionAt(edit.start),
      model.getPositionAt(edit.end),
    );
    editor.pushUndoStop();
    editor.executeEdits("progsity-restore", [
      { range, text: edit.text, forceMoveMarkers: true },
    ]);
    editor.pushUndoStop();
    return true;
  }, []);

  const revealLine = useCallback((line: number, column = 1): boolean => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!Number.isSafeInteger(line) || line < 1) return false;
    if (!editor || !model) { pendingReveal.current = { line, column }; return false; }
    if (line > model.getLineCount()) return false;
    const position = { lineNumber: line, column: Math.max(1, Math.min(column, model.getLineMaxColumn(line))) };
    editor.revealLineInCenter(line);
    editor.setPosition(position);
    editor.focus();
    return true;
  }, []);
  useImperativeHandle(ref, () => ({ format, restore, revealLine }), [format, restore, revealLine]);

  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel();
    if (!editor || !monaco || !model) return;
    const valid = markers.filter((marker) => marker.line > 0 && marker.line <= model.getLineCount());
    monaco.editor.setModelMarkers(model, "progsity-compile", valid.map((marker) => {
      const column = Math.min(marker.column, model.getLineMaxColumn(marker.line));
      return { severity: monaco.MarkerSeverity.Error, startLineNumber: marker.line, endLineNumber: marker.line,
        startColumn: column, endColumn: Math.min(column + 1, model.getLineMaxColumn(marker.line)), message: marker.message };
    }));
    markerDecorations.current?.clear();
    markerDecorations.current = editor.createDecorationsCollection(valid.map((marker) => ({
      range: new monaco.Range(marker.line, model.getLineMaxColumn(marker.line), marker.line, model.getLineMaxColumn(marker.line)),
      // `showIfCollapsed`: the range is empty, at the line's end, and Monaco
      // draws no injected text for an empty range without it, so the message
      // never showed (found 2026-10-02 taking `EX-EDR05`'s pictures).
      options: { showIfCollapsed: true, after: { content: `  ${marker.message.replace(/[\r\n]/g, " ").slice(0, 100)}`, inlineClassName: styles.message } },
    })));
  }, [markers, mounted, language]);

  /** Monaco sometimes mounts before flex height settles (esp. client nav on mobile) — keep model in sync. */
  useLayoutEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const next = value;
    if (ed.getValue() !== next) {
      ed.setValue(next);
    }
  }, [value]);

  const monacoTheme = settings.theme === "dark" ? darkTheme : "light";

  const handleBeforeMount: BeforeMount = useCallback((monaco) => {
    registerProgsityDarkTheme(monaco);
  }, []);

  const handleMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    const syncLayout = () => {
      editor.layout();
    };
    const layoutTwice = () => {
      requestAnimationFrame(() => {
        syncLayout();
        requestAnimationFrame(syncLayout);
      });
    };

    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    const host = containerRef.current;
    if (host && typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => {
        editor.layout();
      });
      ro.observe(host);
      resizeObserverRef.current = ro;
    }
    layoutTwice();

    const initial = valueRef.current;
    if (editor.getValue() !== initial) {
      editor.setValue(initial);
    }
    modelListener.current?.dispose();
    modelListener.current = editor.onDidChangeModelContent(() => {
      const model = editor.getModel();
      if (model) monaco.editor.setModelMarkers(model, "progsity-compile", []);
      markerDecorations.current?.clear();
    });
    setMounted(true);
    const pending = pendingReveal.current;
    if (pending) {
      pendingReveal.current = null;
      revealLine(pending.line, pending.column);
    }

    const narrow = window.matchMedia(NARROW_VIEWPORT_MQ).matches;
    if (narrow) {
      /** Mobile flex + % heights often resolve to 0 on first paint; pump layout until stable. */
      let frames = 0;
      const pump = () => {
        editor.layout();
        frames++;
        if (frames < 20) {
          requestAnimationFrame(pump);
        }
      };
      requestAnimationFrame(pump);

      visualViewportCleanupRef.current?.();
      visualViewportCleanupRef.current = null;
      const vv = window.visualViewport;
      if (vv) {
        const onVv = () => editor.layout();
        vv.addEventListener("resize", onVv);
        vv.addEventListener("scroll", onVv);
        visualViewportCleanupRef.current = () => {
          vv.removeEventListener("resize", onVv);
          vv.removeEventListener("scroll", onVv);
        };
      }

      queueMicrotask(() => editor.layout());
    }

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      onRunRef.current();
    });

    // Monaco's own chord for Format Document. Routed to the owner when it has
    // a handler so the wasm languages and the toast feedback work from the
    // keyboard too; otherwise the built-in action keeps the chord.
    editor.addCommand(monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF, () => {
      const handler = onFormatShortcutRef.current;
      if (handler) {
        handler();
        return;
      }
      void editor.getAction("editor.action.formatDocument")?.run();
    });

    const bump = (delta: number) => {
      const cb = onFontSizeChangeRef.current;
      if (!cb) return;
      const cur = editor.getOption(monaco.editor.EditorOption.fontSize);
      cb(bumpFontSize(cur, delta));
    };

    const resetZoom = () => {
      onFontSizeChangeRef.current?.(DEFAULT_EDITOR_SETTINGS.fontSize);
    };

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Equal, () => bump(1));
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Minus, () => bump(-1));
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.NumpadAdd, () => bump(1));
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.NumpadSubtract, () => bump(-1));
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Digit0, () => resetZoom());

    keyListenerRef.current?.dispose();
    keyListenerRef.current = editor.onKeyDown((e) => {
      onKeyDownRef.current?.(e.browserEvent);
    });

    cleanupWheelRef.current?.();
    cleanupWheelRef.current = null;

    const dom = editor.getDomNode();
    if (dom) {
      const onWheel = (e: WheelEvent) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY < 0 ? 1 : -1;
        const cur = editor.getOption(monaco.editor.EditorOption.fontSize);
        onFontSizeChangeRef.current?.(bumpFontSize(cur, delta));
      };
      dom.addEventListener("wheel", onWheel, { passive: false });
      cleanupWheelRef.current = () => {
        dom.removeEventListener("wheel", onWheel);
      };
    }
  }, [revealLine]);

  const options = useMemo((): EditorProps["options"] => {
    const wordWrapOn = settings.wordWrap || isNarrowViewport;
    return {
      fontSize: settings.fontSize,
      tabSize: settings.tabSize,
      minimap: { enabled: settings.minimap && !isNarrowViewport },
      wordWrap: wordWrapOn ? "on" : "off",
      fontFamily: "var(--font-playground-mono, ui-monospace), monospace",
      scrollBeyondLastColumn: 12,
      scrollBeyondLastLine: false,
      disableMonospaceOptimizations: true,
      automaticLayout: true,
      lineNumbers: "on",
      renderLineHighlight: "line",
      padding: isNarrowViewport ? { top: 8, bottom: 8 } : { top: 12, bottom: 12 },
      mouseWheelZoom: false,
      accessibilitySupport: "auto",
      ariaLabel: resolvedAriaLabel,
      ...(readOnly ? { readOnly: true, domReadOnly: true } : {}),
      ...(contextMenu ? {} : { contextmenu: false }),
    };
  }, [
    readOnly,
    contextMenu,
    isNarrowViewport,
    settings.fontSize,
    settings.minimap,
    settings.tabSize,
    settings.wordWrap,
    resolvedAriaLabel,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex min-w-0 flex-1 flex-col",
        fillParent
          ? "min-h-0"
          : "min-h-[min(34vh,300px)] lg:min-h-0",
        className,
      )}
    >
      <MonacoEditor
        height={
          isNarrowViewport && !fillHeight
            ? "clamp(220px, min(42dvh, 46vh), 400px)"
            : "100%"
        }
        className="min-w-0 flex-1 rounded-lg border border-white/10 lg:flex-none"
        theme={monacoTheme}
        language={language}
        value={value}
        onChange={(v) => onChange(v ?? "")}
        options={options}
        beforeMount={handleBeforeMount}
        onMount={handleMount}
      />
    </div>
  );
}
